import { FC, useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Stack,
  Button,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { RadioButtonUncheckedOutlined, RadioButtonCheckedOutlined } from '@mui/icons-material';
import Search from '../Search';
import { TruncatingTypography } from '@components';
interface TableFilterDropdownProps {
  selected: string[];
  setSelected: (filters: string[]) => void;
  options: { label: string; value: string }[];
  label?: string;
  showSingleSelectIcon?: boolean;
  showSelectAllAndReset?: boolean;
  showSearchInput?: boolean;
  menuMaxWidth?: number | string;
}

const linkButtonSx = {
  padding: 0,
  minWidth: 'auto',
  fontSize: '12px',
  fontWeight: 700,
  color: '#2F736E',
  textTransform: 'none' as const,
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
};

const TableFilterDropdown: FC<TableFilterDropdownProps> = ({
  options,
  selected,
  setSelected,
  label = 'Filter',
  showSingleSelectIcon = false,
  showSelectAllAndReset = false,
  showSearchInput = false,
  menuMaxWidth = 300,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const [search, setSearch] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    if (!options.length) return;
    setFilteredOptions(options);
  }, [options]);

  const handleColumnFilterSearch = (search: string) => {
    setFilteredOptions(
      options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(search.toLowerCase()) || selected.includes(opt.value),
      ),
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
    setFilteredOptions(options);
  };

  const handleSelectAll = () => {
    setSelected(options.map((opt) => opt.value));
    setSearch('');
  };

  const handleReset = () => {
    setSelected([]);
    setSearch('');
    setFilteredOptions(options);
  };

  const handleToggle = (option: string) => {
    const newSelected = [...selected];

    if (selectedSet.has(option)) {
      const index = newSelected.indexOf(option);
      if (index !== -1) {
        newSelected.splice(index, 1);
      }
    } else {
      newSelected.push(option);
    }

    setSelected(newSelected);
  };

  const isOpen = Boolean(anchorEl);
  const menuId = 'filter-menu';

  return (
    <>
      <Box
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}
      >
        {selected.length > 0 && (
          <Box
            sx={{
              width: '1.25rem',
              height: '1.25rem',
              bgcolor: '#FF6347',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#fff',
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              {selected.length}
            </Typography>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={handleClick}
          aria-label={`${label} options`}
          aria-controls={isOpen ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          color={selected.length > 0 ? 'primary' : 'default'}
        >
          <FilterAltOutlinedIcon
            sx={{
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: '#162C36',
            }}
          />
        </IconButton>
      </Box>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'filter-button',
          sx: showSelectAllAndReset
            ? {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 0,
              }
            : undefined,
        }}
        PaperProps={{
          sx: {
            maxHeight: '300px',
            maxWidth: menuMaxWidth,
            width: 'max-content',
            borderRadius: '0.5rem',
            ...(showSelectAllAndReset && {
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }),
            ...(!showSelectAllAndReset && { overflowY: 'auto' }),
          },
        }}
      >
        {showSelectAllAndReset && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            px="12px"
            py="8px"
            sx={{
              borderBottom: '1px solid #737F86',
              flexShrink: 0,
            }}
            gap={1.5}
          >
            <Typography fontSize="12px" fontWeight={700} color="#6C6C6C" marginRight="1.5rem">
              {label}
            </Typography>
            <Stack direction="row" gap={1.5}>
              <Button onClick={handleSelectAll} sx={linkButtonSx}>
                Select All
              </Button>
              <Button onClick={handleReset} sx={linkButtonSx}>
                Reset
              </Button>
            </Stack>
          </Stack>
        )}
        {showSearchInput && (
          <Search
            handleSearch={handleColumnFilterSearch}
            search={search}
            setSearch={setSearch}
            placeholder="Search"
            styles={{
              backgroundColor: '#fff',
            }}
          />
        )}
        <Box sx={showSelectAllAndReset ? { overflowY: 'auto', flex: 1, minHeight: 0 } : undefined}>
          {filteredOptions.map(({ label: optionLabel, value }) => (
            <MenuItem key={value} dense sx={{ maxWidth: '100%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedSet.has(value)}
                    onChange={() => handleToggle(value)}
                    {...(showSingleSelectIcon && {
                      icon: <RadioButtonUncheckedOutlined />,
                      checkedIcon: <RadioButtonCheckedOutlined />,
                    })}
                    inputProps={{ 'aria-label': `Filter by ${optionLabel}` }}
                  />
                }
                label={
                  <TruncatingTypography
                    component="span"
                    tooltipPlacement="top"
                    sx={{
                      display: 'block',
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    {optionLabel}
                  </TruncatingTypography>
                }
                sx={{
                  width: '100%',
                  ml: 0,
                  mr: 0,
                  minWidth: 0,
                  '& .MuiFormControlLabel-label': {
                    minWidth: 0,
                    overflow: 'hidden',
                  },
                }}
              />
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </>
  );
};

export default TableFilterDropdown;
